/**
 * Copy text to the clipboard with a graceful fallback.
 *
 * The async Clipboard API is the happy path, but it fails in non-secure
 * contexts, older Safari, and — relevant to this widget — inside a cross-origin
 * iframe unless the host grants `allow="clipboard-write"` (wired in Phase 5).
 * When it's unavailable or rejects, we fall back to a hidden `<textarea>` +
 * `document.execCommand('copy')`, which still works from a user gesture.
 *
 * Returns whether the copy succeeded so the UI can show an accurate state.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path (e.g. blocked permission, insecure origin).
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Keep it out of view and unfocusable to the user, but selectable for copy.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  } finally {
    document.body.removeChild(textarea);
  }
  return ok;
}
